import { useEffect, useMemo, useRef, useState } from 'react'

import { IAgoraRTCRemoteUser, UID, useRemoteUsers } from 'agora-rtc-react'

import { isScreenShareUserUid } from '../utils/screenShare'

export const useScreenSharePinning = () => {
  const remoteUsers = useRemoteUsers()

  const screenSharingRemoteUsers = useMemo(
    () => remoteUsers.filter(user => isScreenShareUserUid(user.uid)),
    [remoteUsers],
  )
  const isScreenSharingActive = screenSharingRemoteUsers.length > 0

  const [pinnedMainUserId, setPinnedMainUserId] = useState<UID | null>(null)
  const prevScreenShareUidsRef = useRef<Set<UID>>(new Set())

  // remoteUsers/screenSharingRemoteUsers は参照が毎レンダー変わりうるため、
  // useEffect 内では最新値を ref 経由で参照し、依存配列には安定化した UID キーを使う
  const remoteUsersRef = useRef(remoteUsers)
  remoteUsersRef.current = remoteUsers

  const screenSharingRemoteUsersRef = useRef(screenSharingRemoteUsers)
  screenSharingRemoteUsersRef.current = screenSharingRemoteUsers

  const screenShareUidKey = useMemo(
    () => screenSharingRemoteUsers.map(u => u.uid).join(','),
    [screenSharingRemoteUsers],
  )

  const remoteUserUidKey = useMemo(
    () => remoteUsers.map(u => u.uid).join(','),
    [remoteUsers],
  )

  useEffect(() => {
    const currentScreenSharingRemoteUsers = screenSharingRemoteUsersRef.current
    const currentRemoteUsers = remoteUsersRef.current

    if (!isScreenSharingActive) {
      setPinnedMainUserId(null)
      prevScreenShareUidsRef.current = new Set()
      return
    }

    const currentUids = new Set(currentScreenSharingRemoteUsers.map(u => u.uid))

    // 新しく追加された画面共有ユーザーを検出
    const newUsers = currentScreenSharingRemoteUsers.filter(
      u => !prevScreenShareUidsRef.current.has(u.uid),
    )

    prevScreenShareUidsRef.current = currentUids

    if (newUsers.length > 0) {
      // あと勝ち: 最後に追加された画面共有ユーザーをピン留め
      setPinnedMainUserId(newUsers[newUsers.length - 1].uid)
      return
    }

    // 新規ユーザーなし: 既存のピン留めを維持、切断時はフォールバック
    setPinnedMainUserId(prev => {
      if (prev === null) return currentScreenSharingRemoteUsers[0].uid
      const pinnedUserExists = currentRemoteUsers.some(u => u.uid === prev)
      if (!pinnedUserExists)
        return currentScreenSharingRemoteUsers[0]?.uid ?? null
      return prev
    })
  }, [isScreenSharingActive, remoteUserUidKey, screenShareUidKey])

  const mainUser: IAgoraRTCRemoteUser | undefined =
    remoteUsers.find(u => u.uid === pinnedMainUserId) ??
    screenSharingRemoteUsers[0]

  return {
    remoteUsers,
    screenSharingRemoteUsers,
    isScreenSharingActive,
    pinnedMainUserId,
    setPinnedMainUserId,
    mainUser,
  }
}
